/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { or } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import Feature from '@linc-technologies/linced-common/enums/feature';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { observes } from '@ember-decorators/object';

export default class GoalsController extends Controller {
  // Services
  @service
  options;

  @service
  router;

  @service
  session;

  @service
  store;

  @tracked
  selectedCategory = null;

  @optionAlias('core:studentGoalSetting:canCreatePLP')
  studentCanCreatePLP;

  @tracked
  mode = 'current';

  @action
  setMode(value) {
    this.mode = value;
  }

  @tracked
  loadingStates = true;

  @tracked
  loadingSubjects = true;

  @or('loadingStates', 'loadingSubjects')
  loading;

  @tracked
  loadingPlpCategories = true;

  @tracked
  loadingPlpSubcategories = true;

  @tracked
  loadingPlpGoals = true;

  @tracked
  loadingPlpStates = true;

  @or(
    'loadingPlpCategories',
    'loadingPlpSubcategories',
    'loadingPlpGoals',
    'loadingPlpStates',
  )
  loadingPlp;

  @observes('mode')
  _loadPlp() {
    // Reset PLP loading states when changing display mode.
    this.loadingPlpCategories = true;
    this.loadingPlpSubcategories = true;
    this.loadingPlpGoals = true;
    this.loadingPlpStates = true;

    // If new filter is PLP, load the data.
    if (this.mode === 'plp') {
      this.loadPlp();
    }
  }

  @tracked
  loadingSubjectCategories = true;

  @tracked
  loadingSubjectSubcategories = true;

  @tracked
  loadingSubjectGoals = true;

  @tracked
  loadingSubjectStates = true;

  @or(
    'loadingSubjectCategories',
    'loadingSubjectSubcategories',
    'loadingSubjectGoals',
    'loadingSubjectStates',
  )
  loadingSubject;

  @observes('mode', 'subject.id')
  _loadSubject() {
    // Reset Subject loading states when changing display mode.
    this.loadingSubjectCategories = true;
    this.loadingSubjectSubcategories = true;
    this.loadingSubjectGoals = true;
    this.loadingSubjectStates = true;

    // If new filter is Subject, load the data.
    if (this.mode === 'subject' && !isEmpty(this.subject)) {
      this.loadSubject();
    }
  }

  @computed('states.[]', 'cache.[]', 'mode')
  get current() {
    let isPLP = this.mode === 'plp';
    let states = this.states.filter((state) => {
      let isDeveloping = state.get('awarded') === GoalState.DEVELOPING;
      if (isPLP) {
        return isDeveloping && state.get('goal.isPLP') === true;
      } else {
        return isDeveloping;
      }
    });

    if (!isPLP && this.cache.length > 0) {
      states.push(...this.cache);
    }

    return states.sort(
      byKeys(
        'goal.subject.label',
        'goal.category.sequence',
        'goal.subcategory.sequence',
        'goal.sequence',
      ),
    );
  }

  @computed('session.school.featuresEnabled.[]')
  get heroStarterPackEnabled() {
    if (isEmpty(this.session.school)) {
      // School record not loaded yet, so we can't check it.  Return false, and we'll update when the record loads.
      return false;
    }
    return this.session.school.featureEnabled(Feature.STARTER_PACK);
  }

  @computed('session.school.featuresEnabled.[]', 'heroStarterPackEnabled')
  get goalsPowerUpEnabled() {
    if (isEmpty(this.session.school)) {
      // School record not loaded yet, so we can't check it.  Return false, and we'll update when the record loads.
      return false;
    }
    if (!this.heroStarterPackEnabled) {
      return true;
    }
    return this.session.school.featureEnabled(Feature.GOALS_POWER_UP);
  }

  cache = [];

  @tracked
  plpCategories;

  @tracked
  plpGoals;

  @tracked
  plpStates;

  @tracked
  plpSubcategories;

  @tracked
  states;

  @tracked
  subjectCategories;

  @tracked
  subjectGoals;

  @tracked
  subjectStates;

  @tracked
  subjectSubcategories;

  @tracked
  subjects;

  // load fetches the data the route depends on. The route drives this from
  // `setupController` rather than the controller fetching in `init`: a
  // singleton controller that queries as soon as it is looked up leaks
  // in-flight requests into store teardown, and makes the controller
  // impossible to instantiate outside of a live route.
  load() {
    this.loadingStates = true;
    this.loadingSubjects = true;

    this.store
      .query('goals/state', {
        personId: this.get('person.id'),
        current: true,
        include: 'goals',
      })
      .then((states) => {
        this.states = states;
        this.loadingStates = false;
      });

    this.store
      .query('goals/subject', { visibleToTeachers: true })
      .then((subjects) => {
        this.subjects = subjects
          .slice()
          .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
        this.loadingSubjects = false;
      });
  }

  loadPlp() {
    let store = this.store;

    store.query('goals/category', { isPLP: true }).then((categories) => {
      categories = categories
        .slice()
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
      this.plpCategories = categories;
      if (!this.selectedCategory) {
        this.selectedCategory = categories[0];
      }
      this.loadingPlpCategories = false;
    });
    store.query('goals/subcategory', { isPLP: true }).then((subcategories) => {
      this.plpSubcategories = subcategories;
      this.loadingPlpSubcategories = false;
    });
    store
      .query('goals/goal', { personId: this.get('person.id') })
      .then((goals) => {
        this.plpGoals = goals;
        this.loadingPlpGoals = false;
      });
    store
      .query('goals/state', {
        isPLP: true,
        personId: this.get('person.id'),
        current: true,
      })
      .then((states) => {
        this.plpStates = states;
        this.loadingPlpStates = false;
      });
  }

  loadSubject() {
    let store = this.store;
    let subject = this.subject;

    store
      .query('goals/category', { subjectId: subject.id })
      .then((categories) => {
        this.subjectCategories = categories;
        this.loadingSubjectCategories = false;
      });
    store
      .query('goals/subcategory', { subjectId: subject.id })
      .then((subcategories) => {
        this.subjectSubcategories = subcategories;
        this.loadingSubjectSubcategories = false;
      });
    store.query('goals/goal', { subjectId: subject.id }).then((goals) => {
      this.subjectGoals = goals;
      this.loadingSubjectGoals = false;
    });
    store
      .query('goals/state', {
        subjectId: subject.id,
        personId: this.get('person.id'),
        current: true,
      })
      .then((states) => {
        this.subjectStates = states;
        this.loadingSubjectStates = false;
      });
  }

  closeCard() {}

  @computed('session')
  get plpLabel() {
    return (
      this.options.schoolGet(
        this.session.school,
        'core:studentGoalSetting:plpFullLabel',
      ) || 'Personal Learning Plan'
    );
  }

  @computed('session')
  get plpShortLabel() {
    return (
      this.options.schoolGet(
        this.session.school,
        'core:studentGoalSetting:plpShortLabel',
      ) || 'PLP'
    );
  }

  @action
  setGoalSubject(subject) {
    this.setProperties({
      subject,
      mode: 'subject',
    });
  }

  @action
  viewCurrent() {
    this.setProperties({
      cache: [],
      mode: 'current',
      subject: null,
    });
    this.states.update();
  }

  @action
  onClickSave() {
    this.loadPlp();
  }
}
